import Content from '../classes/content.js';
import * as CONSTS from '../constants.js';
import { graphemesOf } from '../text_segment.js';
const { jsPDF } = window.jspdf;
const hrLineHeight = 935;
const hashiraLineOffset = 150;
const hashiraLineOffsetBottom = 2800;
const hashiraLineOffsetSide = 28;
const paperWidth = 4093;

// 埋め込みフォントのcmap(format 12)を解析し、コードポイント収録判定関数を返す。
// 解析できない場合は常にtrue（従来動作）を返す。
function buildFontCoverage(arrayBuffer) {
  try {
    const dv = new DataView(arrayBuffer);
    const numTables = dv.getUint16(4);
    let cmapOffset = -1;
    for (let i = 0; i < numTables; i++) {
      const rec = 12 + i * 16;
      const tag = String.fromCharCode(
        dv.getUint8(rec),
        dv.getUint8(rec + 1),
        dv.getUint8(rec + 2),
        dv.getUint8(rec + 3),
      );
      if (tag === 'cmap') {
        cmapOffset = dv.getUint32(rec + 8);
        break;
      }
    }
    if (cmapOffset < 0) return () => true;
    const numSub = dv.getUint16(cmapOffset + 2);
    let sub12 = -1;
    for (let i = 0; i < numSub; i++) {
      const recOff = cmapOffset + 4 + i * 8;
      const subOff = cmapOffset + dv.getUint32(recOff + 4);
      if (dv.getUint16(subOff) === 12) {
        sub12 = subOff;
        break;
      }
    }
    if (sub12 < 0) return () => true;
    const nGroups = dv.getUint32(sub12 + 12);
    const ranges = [];
    let p = sub12 + 16;
    for (let g = 0; g < nGroups; g++) {
      ranges.push([dv.getUint32(p), dv.getUint32(p + 4)]);
      p += 12;
    }
    // format 12 のグループは startCharCode 昇順なので二分探索できる
    return (cp) => {
      let lo = 0;
      let hi = ranges.length - 1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        const r = ranges[mid];
        if (cp < r[0]) hi = mid - 1;
        else if (cp > r[1]) lo = mid + 1;
        else return true;
      }
      return false;
    };
  } catch (e) {
    return () => true;
  }
}

export default {
  methods: {
    /**
     * pdf出力用のElementを作成する
     * @param  {Array<Content>} contents
     */
    strokeUpperLine(ctx) {
      // パスをリセット
      ctx.beginPath();
      ctx.moveTo(0, hrLineHeight);
      ctx.lineTo(paperWidth, hrLineHeight);
      // 線の太さ
      ctx.lineWidth = 2;
      // 線を描画する
      ctx.stroke();
    },
    /**
     * pdfのページに画像データを貼り付ける
     * 最後のページであれば保存、それ以外は次のページを追加
     * @param {jsPDF} doc
     * @param {string} canvasImageData
     * @param {boolean} isLast
     *
     */
    async addCanvasImageToPdf(doc, canvasImageData, isLast) {
      doc.addImage(canvasImageData, 'png', 0, 0, 297, 210);
      if (isLast) {
        doc.save('document.pdf');
      } else {
        doc.addPage('a4', 'l');
      }
    },
    readTtfTableOffset(dataView, tableTag) {
      const numTables = dataView.getUint16(4, false);
      for (let i = 0; i < numTables; i++) {
        const entryOffset = 12 + i * 16;
        const tag = String.fromCharCode(
          dataView.getUint8(entryOffset),
          dataView.getUint8(entryOffset + 1),
          dataView.getUint8(entryOffset + 2),
          dataView.getUint8(entryOffset + 3),
        );
        if (tag === tableTag) {
          return dataView.getUint32(entryOffset + 8, false);
        }
      }
      return null;
    },
    createTtfGlyphSupportChecker(arrayBuffer) {
      const dataView = new DataView(arrayBuffer);
      const cmapOffset = this.readTtfTableOffset(dataView, 'cmap');
      if (cmapOffset === null) return null;

      const numTables = dataView.getUint16(cmapOffset + 2, false);
      let format12Groups = null;
      const format4Tables = [];

      for (let i = 0; i < numTables; i++) {
        const recordOffset = cmapOffset + 4 + i * 8;
        const subtableRelativeOffset = dataView.getUint32(recordOffset + 4, false);
        const subtableOffset = cmapOffset + subtableRelativeOffset;
        const format = dataView.getUint16(subtableOffset, false);

        if (format === 12 && !format12Groups) {
          const nGroups = dataView.getUint32(subtableOffset + 12, false);
          const groups = [];
          let groupOffset = subtableOffset + 16;
          for (let j = 0; j < nGroups; j++) {
            groups.push({
              start: dataView.getUint32(groupOffset, false),
              end: dataView.getUint32(groupOffset + 4, false),
              glyphStart: dataView.getUint32(groupOffset + 8, false),
            });
            groupOffset += 12;
          }
          format12Groups = groups;
        }

        if (format === 4) {
          const segCount = dataView.getUint16(subtableOffset + 6, false) / 2;
          const endCodeOffset = subtableOffset + 14;
          const startCodeOffset = endCodeOffset + segCount * 2 + 2;
          const idDeltaOffset = startCodeOffset + segCount * 2;
          const idRangeOffsetOffset = idDeltaOffset + segCount * 2;
          const segments = [];

          for (let j = 0; j < segCount; j++) {
            segments.push({
              start: dataView.getUint16(startCodeOffset + j * 2, false),
              end: dataView.getUint16(endCodeOffset + j * 2, false),
              idDelta: dataView.getInt16(idDeltaOffset + j * 2, false),
              idRangeOffset: dataView.getUint16(idRangeOffsetOffset + j * 2, false),
              idRangeOffsetAddress: idRangeOffsetOffset + j * 2,
            });
          }

          format4Tables.push({ segments });
        }
      }

      const supportsInFormat12 = (codePoint) => {
        if (!format12Groups) return false;
        let low = 0;
        let high = format12Groups.length - 1;

        while (low <= high) {
          const mid = Math.floor((low + high) / 2);
          const group = format12Groups[mid];

          if (codePoint < group.start) {
            high = mid - 1;
          } else if (codePoint > group.end) {
            low = mid + 1;
          } else {
            const glyphId = group.glyphStart + (codePoint - group.start);
            return glyphId !== 0;
          }
        }

        return false;
      };

      const supportsInFormat4 = (codePoint) => {
        for (let t = 0; t < format4Tables.length; t++) {
          const { segments } = format4Tables[t];
          let low = 0;
          let high = segments.length - 1;

          while (low <= high) {
            const mid = Math.floor((low + high) / 2);
            const segment = segments[mid];

            if (codePoint > segment.end) {
              low = mid + 1;
              continue;
            }
            if (codePoint < segment.start) {
              high = mid - 1;
              continue;
            }

            if (segment.idRangeOffset === 0) {
              const glyphId = (codePoint + segment.idDelta) & 0xffff;
              return glyphId !== 0;
            }

            const glyphIndexAddress =
              segment.idRangeOffsetAddress + segment.idRangeOffset + (codePoint - segment.start) * 2;
            if (glyphIndexAddress + 1 >= dataView.byteLength) {
              return false;
            }

            let glyphId = dataView.getUint16(glyphIndexAddress, false);
            if (glyphId !== 0) {
              glyphId = (glyphId + segment.idDelta) & 0xffff;
            }
            return glyphId !== 0;
          }
        }

        return false;
      };

      return (char) => {
        if (!char || /^\s$/.test(char)) return true;
        const codePoint = char.codePointAt(0);
        if (codePoint === undefined) return true;

        if (format12Groups && format12Groups.length > 0) {
          return supportsInFormat12(codePoint);
        }

        if (codePoint <= 0xffff && format4Tables.length > 0) {
          return supportsInFormat4(codePoint);
        }

        return true;
      };
    },
    async getPdfFontGlyphSupportChecker(fontPath) {
      if (!this.__pdfFontGlyphSupportCache) {
        this.__pdfFontGlyphSupportCache = {};
      }
      if (Object.prototype.hasOwnProperty.call(this.__pdfFontGlyphSupportCache, fontPath)) {
        return this.__pdfFontGlyphSupportCache[fontPath];
      }

      const buffer = await this.getPdfFontArrayBuffer(fontPath);
      if (!buffer) {
        this.__pdfFontGlyphSupportCache[fontPath] = null;
        return null;
      }

      const checker = this.createTtfGlyphSupportChecker(buffer);
      this.__pdfFontGlyphSupportCache[fontPath] = checker;
      return checker;
    },
    async getPdfFontArrayBuffer(fontPath) {
      if (!this.__pdfFontArrayBufferCache) {
        this.__pdfFontArrayBufferCache = {};
      }
      if (Object.prototype.hasOwnProperty.call(this.__pdfFontArrayBufferCache, fontPath)) {
        return this.__pdfFontArrayBufferCache[fontPath];
      }

      try {
        const response = await fetch(fontPath);
        if (!response.ok) {
          this.__pdfFontArrayBufferCache[fontPath] = null;
          return null;
        }
        const buffer = await response.arrayBuffer();
        this.__pdfFontArrayBufferCache[fontPath] = buffer;
        return buffer;
      } catch (error) {
        this.__pdfFontArrayBufferCache[fontPath] = null;
        return null;
      }
    },
    async getPdfImageAsset(imagePath) {
      if (!this.__pdfImageAssetCache) {
        this.__pdfImageAssetCache = {};
      }
      if (Object.prototype.hasOwnProperty.call(this.__pdfImageAssetCache, imagePath)) {
        return this.__pdfImageAssetCache[imagePath];
      }

      try {
        const response = await fetch(imagePath);
        if (!response.ok) {
          this.__pdfImageAssetCache[imagePath] = null;
          return null;
        }
        const blob = await response.blob();
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => reject(new Error('Failed to read image blob'));
          reader.readAsDataURL(blob);
        });
        if (typeof dataUrl !== 'string') {
          this.__pdfImageAssetCache[imagePath] = null;
          return null;
        }

        const imageInfo = await new Promise((resolve, reject) => {
          const image = new Image();
          image.onload = () => {
            resolve({
              width: image.naturalWidth || image.width,
              height: image.naturalHeight || image.height,
            });
          };
          image.onerror = () => reject(new Error('Failed to load image data URL'));
          image.src = dataUrl;
        });

        const asset = {
          dataUrl,
          width: imageInfo.width,
          height: imageInfo.height,
        };
        this.__pdfImageAssetCache[imagePath] = asset;
        return asset;
      } catch (error) {
        this.__pdfImageAssetCache[imagePath] = null;
        return null;
      }
    },
    normalizeGlyphForTextPdf(glyph) {
      if (!glyph) return '';
      // 実描画時に除去している不可視制御文字を事前判定でも同じように除去する
      // VS Supplement(U+E0100-U+E01EF) も除去して、IVS付き漢字が不必要に □ 置換されるのを防ぐ
      return glyph.replace(/[\u200D\uFE00-\uFE0F\uFEFF\u{E0100}-\u{E01EF}]/gu, '');
    },
    canRenderGlyphInTextPdf(glyph, isGlyphSupported) {
      const normalizedGlyph = this.normalizeGlyphForTextPdf(glyph);
      if (!normalizedGlyph) return true;

      for (const char of normalizedGlyph) {
        const codePoint = char.codePointAt(0);
        if (codePoint === undefined) continue;
        // jsPDF描画側の制約に合わせる（BMP外は現状 □ 置換）
        if (codePoint > 0xffff) return false;
        if (!isGlyphSupported(char)) return false;
      }

      return true;
    },
    collectUnsupportedGlyphsByPage(firstContent, isGlyphSupported) {
      if (typeof isGlyphSupported !== 'function') return [];

      const gyo = CONSTS.MAX_LINES_PER_PAGE;

      var t_height = 23;
      t_height--;
      var c = 0;
      var v = 1;
      let page = 1;
      const unsupportedGlyphMap = new Map();

      let content = firstContent;
      while (content) {
        const val = content.body == '' ? ' ' : content.body;
        let isHashira = 0;

        switch (content.tag_type) {
          case 'togaki':
            t_height = CONSTS.TOGAKI_MAX_COUNT - 1;
            break;
          case 'serifu':
            t_height = CONSTS.FIRST_SERIFU_MAX_COUNT - 1;
            break;
          case 'hashira':
            isHashira = 1;
            t_height = CONSTS.HASHIRA_MAX_COUNT - 1;
            v++;
            break;
          default:
        }

        const glyphs = graphemesOf(val);

        for (const glyph of glyphs) {
          const baseChar = Array.from(glyph)[0] || glyph;

          if (c > t_height && !(c == t_height + 1 && baseChar.match(/、|。|」|）|』|】|゛/))) {
            if (content.tag_type == 'serifu') {
              t_height = CONSTS.SERIFU_MAX_COUNT - 1;
            }
            c = 0;
            v++;
          }

          if (v > gyo) {
            page++;
            c = 0;
            v = 1;
          }

          if (isHashira == 1) {
            isHashira++;
            if (v == 1) v++;
          }

          const isSupported = this.canRenderGlyphInTextPdf(glyph, isGlyphSupported);
          if (!isSupported) {
            if (!unsupportedGlyphMap.has(glyph)) {
              unsupportedGlyphMap.set(glyph, new Set());
            }
            unsupportedGlyphMap.get(glyph).add(page);
          }

          c++;
        }

        c = 0;
        v++;
        if (isHashira != 0) {
          v++;
        }

        content = content.next;
      }

      return Array.from(unsupportedGlyphMap.entries()).map(([character, pages]) => ({
        character,
        pages: Array.from(pages).sort((a, b) => a - b),
      }));
    },
    async detectUnsupportedGlyphsForTextPdf(firstContent, fontPath) {
      const checker = await this.getPdfFontGlyphSupportChecker(fontPath);
      if (!checker) return [];
      return this.collectUnsupportedGlyphsByPage(firstContent, checker);
    },
    /**
     * pdfの保存処理（画像）
     * テキストベースの保存ができたので使わないが一応残しておく
     * @param {Array<Content>} contents
     *
     */
    async saveContentAsPdf(contents) {
      const doc = new jsPDF({
        orientation: 'l',
        unit: 'mm',
        format: 'a4',
      });
      let pdfCanvas = document.createElement('canvas');
      pdfCanvas.width = paperWidth;
      pdfCanvas.height = 2894;
      var pdfCtx = pdfCanvas.getContext('2d');
      pdfCtx.textAlign = 'center';
      this.strokeUpperLine(pdfCtx);
      var hp = 0;
      var vp = 0;
      var hps_base = 3833; // スタート地点のx座標
      var hps = hps_base;
      var vps_base = 950; // スタート地点のy座標
      var vps = vps_base;
      var chr = '';
      var hp_flag;
      var rubyFlag;
      var rubyVps;
      var rubyHps;
      var rubyCount;
      var rubyLength = 0;
      var rubyWidth = 0;
      var dancount = 0;
      var dan = 0; // 段組の有無（有 = 1）
      var t_height = 23; // １行文字数;
      t_height--;
      var fontname = '游明朝 Medium'; // CSSのfont-familyに対応したフォント名
      var fontsize = 65; // フォントサイズ
      var fontcolor = '#000000'; // 文字色
      var lineheight = 1.68; // 行間
      let chrSpaceRatio = 1.2;
      var gyo = CONSTS.MAX_LINES_PER_PAGE; // 行数
      let next;
      let prev;
      let difH;
      let difV;

      var val = '';

      var c = 0;
      var v = 1;

      contents.forEach((content) => {
        val = content.body == '<br>' ? ' ' : content.body;
        let isHashira = 0;
        let hashiraStart = 0;
        // タグタイプ毎に行頭の高さ、一行も文字数を設定する
        switch (content.tag_type) {
          case 'togaki':
            vps_base = hrLineHeight + 450;
            t_height = CONSTS.TOGAKI_MAX_COUNT - 1;
            break;
          case 'serifu':
            vps_base = hrLineHeight + fontsize * chrSpaceRatio * 0.5;
            t_height = CONSTS.FIRST_SERIFU_MAX_COUNT - 1;
            break;
          case 'hashira':
            isHashira = 1;
            vps_base = hrLineHeight + fontsize * chrSpaceRatio * 0.5;
            t_height = CONSTS.HASHIRA_MAX_COUNT - 1;
            v++; // 行カウント
            break;
          default:
        }
        vps = vps_base;
        // 一文字ずつ処理
        for (var i = 0; i < val.length; i++) {
          pdfCtx.font = fontsize + "px '" + fontname + "', serif";
          pdfCtx.fillStyle = fontcolor;
          vp = fontsize;
          chr = val.charAt(i);
          next = val.charAt(i + 1);
          // if (chr == '｜') { // ルビ処理（「｜漢字《かんじ》」の書式に対応）
          //     rubyFlag = 1;
          //     rubyVps = vps_base + (fontsize * c) -fontsize * 0.4;
          //     rubyHps = hps + (fontsize * 1.03);
          //     rubyCount = c - 0.5;
          //     continue; // 次へ
          // } else if (rubyFlag && chr == '＠') {
          //     rubyFlag = 3;
          //     continue; // 次へ
          // } else if (rubyFlag == 3) {
          //     switch (chr) {
          //         case '１': rubyVps -= fontsize * 0.5;
          //             rubyCount -= 0.5;
          //             break;
          //         case '２': rubyVps -= fontsize;
          //             rubyCount -= 1;
          //             break;
          //         case '３': rubyVps -= fontsize * 1.5;
          //             rubyCount -= 1.5;
          //             break;
          //         case '４': rubyVps -= fontsize * 2;
          //             rubyCount -= 2;
          //             break;
          //         case '５': rubyVps -= fontsize * 2.5;
          //             rubyCount -= 2.5;
          //             break;
          //         case '６': rubyVps -= fontsize * 3;
          //             rubyCount -= 3;
          //             break;
          //         case '７': rubyVps -= fontsize * 3.5;
          //             rubyCount -= 3.5;
          //             break;
          //         case '８': rubyVps -= fontsize * 4;
          //             rubyCount -= 4;
          //             break;
          //         case '９': rubyVps -= fontsize * 4.5;
          //             rubyCount -= 4.5;
          //             break;
          //     }
          //     rubyFlag = 1;
          //     continue; // 次へ
          // } else if (rubyFlag && (chr == '≪' || chr == '《')) {
          //     rubyFlag = 2;
          //     continue; // 次へ
          // } else if (rubyFlag && (chr == '≫' || chr =='》')) {
          //     rubyFlag =0;
          //     rubyCount=0;
          //     rubyLength=0;
          //     rubyWidth = 0;
          //     continue; // 次へ
          // }
          // if (rubyFlag == 1) {
          //     rubyWidth++;
          // }
          // if (rubyFlag == 2) {
          //     if (chr == '圏') { // 圏点処理（「｜漢字《圏》」の書式に対応）
          //       pdfCtx.font = fontsize * 0.3  + "px '" + fontname + "', serif";
          //       // pdfCtx.font = fontsize * 0.3  + "px '" + fontname + "'";
          //       rubyVps = rubyVps + fontsize * 0.3;
          //         if (rubyCount > t_height) {
          //             rubyVps = vps_base - fontsize * 0.1;
          //             rubyHps -= fontsize * lineheight; // 改行
          //         }
          //         for (k = 0; k < rubyWidth; k++) {
          //             pdfCtx.fillText("\uFE45", rubyHps, rubyVps );
          //             rubyVps = rubyVps + fontsize;
          //             if (rubyVps > vps_base + t_height * fontsize) {
          //                 rubyVps = vps_base - fontsize * 0.3;
          //                 rubyHps -= fontsize * lineheight; // 改行
          //             }
          //         }
          //         continue;
          //     }
          //     if (rubyCount > t_height) {
          //         rubyCount = -0.5;
          //         rubyVps = vps_base - fontsize * 0.4;
          //         rubyHps -= fontsize * lineheight; // 改行
          //     }
          //     next = val.charAt(i+1);
          //     prev = val.charAt(i-1);
          //     if ( prev.match(/[《≪]/) && next.match(/[》≫]/) && rubyLength == 1) {
          //         rubyVps = rubyVps + (fontsize * 0.2);
          //     }
          //     pdfCtx.font = fontsize * 0.5  + "px '" + fontname + "', serif";
          //     // pdfCtx.font = fontsize * 0.5  + "px '" + fontname + "'";
          //     pdfCtx.fillText(chr, rubyHps, rubyVps );
          //     rubyVps = rubyVps + fontsize * 0.5;
          //     rubyCount = rubyCount + 0.5;
          //     continue;
          // } else if (rubyFlag == 1) {
          //     rubyLength++;
          // }

          // 一行分の文字数を超えた、もしくは行の頭にこない文字が行の頭になってしまう場合
          if (c > t_height || (c > t_height - 1 && next.match(/、|。|」|）|』|】|゛/))) {
            if (content.tag_type == 'serifu') {
              vps_base = hrLineHeight + fontsize * chrSpaceRatio * 3.5;
              t_height = CONSTS.SERIFU_MAX_COUNT - 1;
            }
            hps -= fontsize * lineheight; // 改行
            vps = vps_base; // 行頭へ
            c = 0; // 縦位置カウント
            v++; // 行カウント
            // }
          } else {
            vps = vps_base + fontsize * chrSpaceRatio * c; // 位置
          }

          // 改ページ処理
          if (v > gyo) {
            if (hashiraStart != 0) {
              pdfCtx.fillText(content.hashira_index, hps + (hashiraStart - hps) / 2, hrLineHeight - 50);
              hashiraStart = 0;
            }
            this.addCanvasImageToPdf(doc, pdfCanvas.toDataURL('image/png'), false);
            pdfCtx.clearRect(0, 0, pdfCanvas.width, pdfCanvas.height);
            this.strokeUpperLine(pdfCtx);
            c = 0;
            v = 1;
            hps = hps_base;
            vps = vps_base;
          }
          // 柱で最初の文字を処理するとき柱ボックスの右辺の描画を行う
          if (isHashira == 1) {
            hashiraStart = hps; // 柱番号描画のために柱の開始x座標を記録
            pdfCtx.beginPath();
            pdfCtx.moveTo(hps, hashiraLineOffsetBottom);
            pdfCtx.lineTo(hps, hrLineHeight - hashiraLineOffset);
            pdfCtx.stroke();
            hps -= fontsize * lineheight; // 改行
            isHashira++;
          }
          // 柱で行の頭の文字の処理を行うとき柱ボックスの上辺の描画を行う
          if (c == 0 && isHashira != 0) {
            pdfCtx.beginPath();
            pdfCtx.moveTo(hps + fontsize * lineheight, hrLineHeight - hashiraLineOffset);
            pdfCtx.lineTo(hps - fontsize * (lineheight - 1) - fontsize, hrLineHeight - hashiraLineOffset);
            pdfCtx.stroke();
          }
          // CSSを使わない（横書き用グリフになる）場合の処理
          pdfCtx.save();
          let Bhps = hps;
          let Bvps = vps;
          switch (true) {
            case /[、。]/.test(chr):
              hps = hps + fontsize * 0.7;
              vps = vps - fontsize * 0.7;
              break;
            case /[ぁぃぅぇぉっゃゅょゎァィゥェォッャュョヵヶ]/.test(chr):
              hps = hps + fontsize * 0.1;
              vps = vps - fontsize * 0.1;
              break;
            case /[「」『』｛｝［］（）＜＞【】〔〕≪≫＜＞～―…：；]/.test(chr):
              pdfCtx.translate(hps, vps);
              pdfCtx.rotate((90 * Math.PI) / 180);
              vps = -(fontsize * 0.1);
              hps = -(fontsize * 0.1);
              break;
            case /[ー]/.test(chr):
              pdfCtx.translate(hps, vps);
              pdfCtx.rotate((91.5 * Math.PI) / 180);
              vps = -(fontsize * 0.1);
              hps = -(fontsize * 0.1);
              break;
            case /[〝]/.test(chr):
              hps = hps - fontsize * 0.5;
              vps = vps + fontsize * 0.5;
              break;
            case /[〟]/.test(chr):
              hps = hps + fontsize * 0.5;
              vps = vps - fontsize * 0.5;
              break;
          }
          difH = hps - Bhps;
          difV = Bvps - vps;

          vps = vps + fontsize * 0.5;

          // 文字を描画する
          if (chr == '！' || chr == '？') {
            // 「！！」「！？」の縦中横処理
            if (next == '！' || next == '？') {
              pdfCtx.fillText(chr, hps - fontsize * 0.25, vps);
              pdfCtx.fillText(next, hps + fontsize * 0.25, vps);
              i++;
            } else {
              pdfCtx.fillText(chr, hps, vps);
            }
            c++;
          } else if (chr == '゛') {
            // 濁点をつける
            vps = vps - fontsize;
            pdfCtx.fillText(chr, hps + fontsize * 0.8, vps);
          } else {
            // if (chr.match(/[A-Za-z0-9"'\.\, \[\]:;!\?]/)) { // 半角文字の回転処理
            //     let w = pdfCtx.measureText(chr);
            //     let p = w.width / fontsize;
            //     pdfCtx.save();
            //     pdfCtx.setTransform(1, 0, 0, 1, 0, 0);
            //     pdfCtx.rotate(90 * Math.PI / 180);
            //     pdfCtx.fillText(chr, vps - fontsize * 0.75 , -hps- fontsize * 0.25);
            //     pdfCtx.restore();
            //     c = c + p*1.1;
            // } else {
            //     pdfCtx.fillText(chr, hps, vps );
            //     if (chr.match(/、|。/) && next.match(/」|』/)){ // 禁則処理
            //       c += 0.5;
            //     } else {
            //       c++;
            //     }
            // }
            pdfCtx.fillText(chr, hps, vps);
            c++;
          }
          hps = hps - difH;
          vps = vps + difV;
          pdfCtx.restore();
        }
        hps -= fontsize * lineheight; // 改行
        vps = vps_base; // 行頭へ
        c = 0;
        v++;
        // 柱ボックスの左辺の描画
        if (isHashira != 0) {
          pdfCtx.beginPath();
          pdfCtx.moveTo(hps, hrLineHeight - hashiraLineOffset);
          pdfCtx.lineTo(hps, hashiraLineOffsetBottom);
          pdfCtx.stroke();
          // 柱番号の描画を行う
          // ページを跨ぐ柱の場合、最初のページには柱番号を描画するがそれ以降は描画しない
          if (hashiraStart != 0) {
            pdfCtx.fillText(content.hashira_index, hps + (hashiraStart - hps) / 2, hrLineHeight - 40);
          }
          hps -= fontsize * lineheight;
          v++;
        }
      });
      // 第3引数がtrueなのでsave処理も行う
      this.addCanvasImageToPdf(doc, pdfCanvas.toDataURL('image/png'), true);
      return true;
    },
    /**
     * pdfの保存処理（テキスト）
     * @param {Array<Content>} contents
     *
     */
    async saveContentAsTextPdf(firstContent, options = {}) {
      try {
        const { skipUnsupportedGlyphCheck = false } = options;
        const fontPath = '/fonts/NotoSerifJP-Light.ttf';
        if (!skipUnsupportedGlyphCheck) {
          const unsupportedGlyphs = await this.detectUnsupportedGlyphsForTextPdf(firstContent, fontPath);
          if (unsupportedGlyphs.length > 0 && typeof this.confirmPdfUnsupportedGlyphs === 'function') {
            const shouldContinue = await this.confirmPdfUnsupportedGlyphs(unsupportedGlyphs);
            if (!shouldContinue) {
              return false;
            }
          }
        }

        const doc = new jsPDF({
          orientation: 'l',
          unit: 'mm',
          format: 'a4',
        });
        let ratio = 297 / paperWidth;
        var hp = 0;
        var vp = 0;
        var chr = '';
        var hp_flag;
        var rubyFlag;
        var rubyVps;
        var rubyHps;
        var rubyCount;
        var rubyLength = 0;
        var rubyWidth = 0;
        var dancount = 0;
        var dan = 0; // 段組の有無（有 = 1）
        var t_height = 23; // １行文字数;
        t_height--;
        var fontsize = 60; // フォントサイズ
        const fontName = 'NotoSerifJP-Light';
        var fontcolor = '#000000'; // 文字色
        var lineheight = 1.75; // 行間
        var hps_base = paperWidth - (paperWidth - fontsize * lineheight * (CONSTS.MAX_LINES_PER_PAGE - 1)) / 2; // スタート地点のx座標
        var hps = hps_base;
        var vps_base = 1200; // スタート地点のy座標
        var vps = vps_base;
        let chrSpaceRatio = 1;
        var gyo = CONSTS.MAX_LINES_PER_PAGE; // 行数
        let next;
        let prev;
        let difH;
        let difV;
        // 最初のページの上の線を描画
        doc.line(
          (paperWidth - hps_base - fontsize * lineheight - 5) * ratio,
          hrLineHeight * ratio,
          (hps_base + fontsize * lineheight + 5) * ratio,
          hrLineHeight * ratio,
        );

        // フォント読み込み＆設定処理
        // Base64の状態でファイルにしておくのも手かも
        function arrayBufferToBinaryString(arrayBuffer) {
          let binaryString = '';
          const bytes = new Uint8Array(arrayBuffer);
          const len = bytes.byteLength;
          for (let i = 0; i < len; i++) {
            binaryString += String.fromCharCode(bytes[i]);
          }
          return binaryString;
        }
        const buffer = await this.getPdfFontArrayBuffer(fontPath);
        if (!buffer) return false;
        const isFontCovered = buildFontCoverage(buffer);
        const binaryString = arrayBufferToBinaryString(buffer);
        const encodedData = btoa(binaryString);
        doc.addFileToVFS('NotoSerifJP-Light.ttf', encodedData);
        doc.addFont('NotoSerifJP-Light.ttf', 'NotoSerif', 'normal', 'Identity-H');
        doc.setFont('NotoSerif');
        doc.setFontSize(12);
        const logoAsset = await this.getPdfImageAsset('/tellastry_logo_ja_gray.png');
        const logoWidth = 24;
        const logoHeight = logoAsset && logoAsset.width > 0 ? (logoWidth * logoAsset.height) / logoAsset.width : 6;
        const logoLeft = 8;
        const logoBottom = 3;

        var val = '';

        var c = 0;
        var v = 1;

        let content = firstContent;
        while (content) {
          val = content.body == '' ? ' ' : content.body;
          let isHashira = 0;
          let hashiraStart = 0;
          // タグタイプ毎に行頭の高さ、一行も文字数を設定する
          switch (content.tag_type) {
            case 'togaki':
              vps_base = hrLineHeight + fontsize * chrSpaceRatio * 7.2;
              t_height = CONSTS.TOGAKI_MAX_COUNT - 1;
              break;
            case 'serifu':
              vps_base = hrLineHeight + fontsize * chrSpaceRatio * 1.2;
              t_height = CONSTS.FIRST_SERIFU_MAX_COUNT - 1;
              break;
            case 'hashira':
              isHashira = 1;
              vps_base = hrLineHeight + fontsize * chrSpaceRatio * 1.2;
              t_height = CONSTS.HASHIRA_MAX_COUNT - 1;
              v++; // 行カウント
              break;
            default:
          }
          vps = vps_base;
          // 一文字ずつ処理（書記素単位。styleのindexも書記素基準）
          const graphemes = graphemesOf(val);
          for (var i = 0; i < graphemes.length; i++) {
            vp = fontsize;
            chr = graphemes[i];
            // 異体字セレクタ・ZWJ等の非表示制御文字はPDFフォントにグリフが無く豆腐になるため除去
            chr = this.normalizeGlyphForTextPdf(chr);
            // jsPDF2.5.1はBMP外(サロゲートペア)を描画できず、フォント未収録文字も豆腐になるため代替記号1マスに置換
            for (const cp of chr) {
              const code = cp.codePointAt(0);
              if (code > 0xffff || !isFontCovered(code)) {
                chr = '□';
                break;
              }
            }
            let textOptions = { align: 'center', angle: 0 };
            // 長音文字（ー）の対応用
            let isMirror = false;

            // 一行分の文字数を超えた、もしくは行の頭にこない文字が行の頭になってしまう場合
            if (c > t_height && !(c == t_height + 1 && chr.match(/、|。|」|）|』|】|゛/))) {
              if (content.tag_type == 'serifu') {
                vps_base = hrLineHeight + fontsize * chrSpaceRatio * 5.2;
                t_height = CONSTS.SERIFU_MAX_COUNT - 1;
              }
              hps -= fontsize * lineheight; // 改行
              vps = vps_base; // 行頭へ
              c = 0; // 縦位置カウント
              v++; // 行カウント
            } else {
              vps = vps_base + fontsize * chrSpaceRatio * c; // 位置
            }

            // 改ページ処理
            if (v > gyo) {
              if (hashiraStart != 0) {
                doc.text(
                  content.hashira_index.toString(),
                  (hps + (hashiraStart - hps) / 2) * ratio,
                  (hrLineHeight - 50) * ratio,
                  textOptions,
                );
                hashiraStart = 0;
              }
              doc.addPage('a4', 'l');
              doc.setDrawColor(0, 0, 0);
              doc.line(
                (paperWidth - hps_base - fontsize * lineheight - 5) * ratio,
                hrLineHeight * ratio,
                (hps_base + fontsize * lineheight + 5) * ratio,
                hrLineHeight * ratio,
              );
              c = 0;
              v = 1;
              hps = hps_base;
              vps = vps_base;
            }
            // 柱で最初の文字を処理するとき柱ボックスの右辺の描画を行う
            if (isHashira == 1) {
              hashiraStart = hps; // 柱番号描画のために柱の開始x座標を記録
              doc.setDrawColor(0, 0, 0);
              doc.line(
                (hps - hashiraLineOffsetSide) * ratio,
                hashiraLineOffsetBottom * ratio,
                (hps - hashiraLineOffsetSide) * ratio,
                (hrLineHeight - hashiraLineOffset) * ratio,
              );
              hps -= fontsize * lineheight; // 改行
              isHashira++;
              // ページの先頭に柱が来る場合
              if (v == 1) v++;
            }
            // 柱で行の頭の文字の処理を行うとき柱ボックスの上辺の描画を行う
            if (c == 0 && isHashira != 0) {
              doc.setDrawColor(0, 0, 0);
              doc.line(
                (hps + fontsize * lineheight - hashiraLineOffsetSide) * ratio,
                (hrLineHeight - hashiraLineOffset) * ratio,
                (hps - fontsize * (lineheight - 1) - fontsize + hashiraLineOffsetSide) * ratio,
                (hrLineHeight - hashiraLineOffset) * ratio,
              );
            }
            // CSSを使わない（横書き用グリフになる）場合の処理
            let Bhps = hps;
            let Bvps = vps;
            switch (true) {
              case /[、。]/.test(chr):
                hps = hps + fontsize * 0.6;
                vps = vps - fontsize * 0.55;
                break;
              case /[ぁぃぅぇぉっゃゅょゎァィゥェォッャュョヵヶ]/.test(chr):
                hps = hps + fontsize * 0.1;
                vps = vps - fontsize * 0.1;
                break;
              case /[「」『』｛｝［］（）＜＞【】〔〕≪≫＜＞…：；]/.test(chr):
                textOptions.angle = 270;
                vps = vps - fontsize * 0.8;
                hps = hps - fontsize * -0.15;
                break;
              case /[ー]/.test(chr):
                textOptions.angle = 268;
                vps = vps - fontsize * 0.85;
                hps = hps - fontsize * 0.15;
                isMirror = true;
                break;
              case /[〜～―]/.test(chr):
                textOptions.angle = 270;
                vps = vps - fontsize * 0.85;
                hps = hps - fontsize * 0.1;
                isMirror = true;
                break;
              case /[〝]/.test(chr):
                hps = hps - fontsize * 0.5;
                vps = vps + fontsize * 0.5;
                break;
              case /[〟]/.test(chr):
                hps = hps + fontsize * 0.5;
                vps = vps - fontsize * 0.5;
                break;
            }
            difH = hps - Bhps;
            difV = Bvps - vps;

            vps = vps + fontsize * 0.5;

            // 文字を描画する
            if ((chr == '！' || chr == '？') && false) {
              //今後対応の可能性を考えて残しておく
              // 「！！」「！？」の縦中横処理
              if (next == '！' || next == '？') {
                doc.text(chr, (hps - fontsize * 0.25) * ratio, vps * ratio, textOptions);
                doc.text(chr, (hps - fontsize * 0.25) * ratio, vps * ratio, textOptions);
                i++;
              } else {
                doc.text(chr, hps * ratio, vps * ratio, textOptions);
              }
              c++;
            } else if (chr == '゛' && false) {
              //今後対応の可能性を考えて残しておく
              // 濁点をつける
              vps = vps - fontsize;
              doc.text(chr, (hps + fontsize * 0.8) * ratio, vps * ratio, textOptions);
            } else {
              let isTorikeshi = false;
              content.style.forEach((style) => {
                if (i >= style.start_index && i <= style.end_index) {
                  if (style.style_type == 'text_color') {
                    doc.setTextColor(255, 0, 0);
                    doc.setDrawColor(255, 0, 0);
                  }
                  if (style.style_type == 'torikeshi_line') {
                    isTorikeshi = true;
                  }
                }
              });
              if (isMirror) {
                doc.internal.write('q'); // 状態保存
                doc.internal.write('-1 0 0 1 0 0 cm'); // X軸反転
                // X座標(hps * ratio)にマイナスをつけるだけで、
                // align: 'center' を軸に綺麗にその場で鏡像化される
                doc.text(chr, -(hps * ratio), vps * ratio, textOptions);
                doc.internal.write('Q'); // 状態復元
              } else {
                // 通常の描画
                doc.text(chr, hps * ratio, vps * ratio, textOptions);
              }
              if (isTorikeshi) {
                doc.line(
                  (hps_base - (v - 1) * fontsize * lineheight) * ratio,
                  (vps_base + (c + 0.6) * fontsize - fontsize) * ratio,
                  (hps_base - (v - 1) * fontsize * lineheight) * ratio,
                  (vps_base + (c + 0.6) * fontsize) * ratio,
                );
              }
              doc.setTextColor(0, 0, 0);
              doc.setDrawColor(0, 0, 0);
              c++;
            }
            hps = hps - difH;
            vps = vps + difV;
          }
          hps -= fontsize * lineheight; // 改行
          vps = vps_base; // 行頭へ
          c = 0;
          v++;
          // 柱ボックスの左辺の描画
          if (isHashira != 0) {
            doc.line(
              (hps + hashiraLineOffsetSide) * ratio,
              hashiraLineOffsetBottom * ratio,
              (hps + hashiraLineOffsetSide) * ratio,
              (hrLineHeight - hashiraLineOffset) * ratio,
            );
            // 柱番号の描画を行う
            // ページを跨ぐ柱の場合、最初のページには柱番号を描画するがそれ以降は描画しない
            if (hashiraStart != 0) {
              doc.text(
                content.hashira_index.toString(),
                (hps + (hashiraStart - hps) / 2) * ratio,
                (hrLineHeight - 50) * ratio,
                { align: 'center' },
              );
            }
            hps -= fontsize * lineheight;
            v++;
          }
          content = content.next;
        }
        var pageCount = doc.internal.getNumberOfPages(); //Total Page Number
        for (var i = 0; i < pageCount; i++) {
          doc.setPage(i);
          let pageCurrent = doc.internal.getCurrentPageInfo().pageNumber; //Current Page
          if (logoAsset) {
            doc.addImage(
              logoAsset.dataUrl,
              'PNG',
              logoLeft,
              doc.internal.pageSize.height - logoHeight - logoBottom,
              logoWidth,
              logoHeight,
            );
          }
          doc.setFontSize(12);
          doc.text('-' + pageCurrent + '-', doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 3, {
            align: 'center',
          });
        }
        // 第3引数がtrueなのでsave処理も行う
        doc.save(`${this.title}.pdf`);
        return true;
      } catch (error) {
        return false;
      }
    },
  },
};
